/**
 * Helper utilities for looking up skill/industry names from API data.
 * Use with useSkills and useIndustries hooks - pass the list and ID(s) to get display names.
 */

export interface SkillOrIndustryItem {
  _id: string;
  name?: string;
  name_ar?: string;
  [key: string]: unknown;
}

/**
 * Get skill name by ID from API skills list.
 * Handles both string and number IDs for backward compatibility.
 */
export function getSkillNameById(
  skills: SkillOrIndustryItem[],
  id: string | number
): string {
  const idStr = String(id);
  const found = skills.find(
    (s) => s._id === idStr || s._id === id || String(s._id) === idStr
  );
  return found?.name ?? "";
}

/**
 * Get industry name by ID from API industries list.
 * Handles both string and number IDs for backward compatibility.
 */
export function getIndustryNameById(
  industries: SkillOrIndustryItem[],
  id: string | number
): string {
  const idStr = String(id);
  const found = industries.find(
    (i) => i._id === idStr || i._id === id || String(i._id) === idStr
  );
  return found?.name ?? "";
}

/**
 * Format array of skill IDs as comma-separated names.
 * Accepts single ID (string | number) or array of IDs.
 */
export function formatSkillsDisplay(
  skills: SkillOrIndustryItem[],
  ids: (string | number)[] | string | number | undefined
): string {
  const idArray = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
  if (idArray.length === 0) return "-";
  const names = idArray
    .map((id) => getSkillNameById(skills, id))
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "-";
}

/**
 * Format array of industry IDs as comma-separated names.
 * Accepts single ID (string | number) or array of IDs.
 */
export function formatIndustriesDisplay(
  industries: SkillOrIndustryItem[],
  ids: (string | number)[] | string | number | undefined
): string {
  const idArray = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
  if (idArray.length === 0) return "-";
  const names = idArray
    .map((id) => getIndustryNameById(industries, id))
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "-";
}
